import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/* ================= TYPES ================= */

export interface LeadCardFunctionality {
  isNameShown: boolean;
  isMobileNumberShown: boolean;
  isBranchShown: boolean;
  isStageShown: boolean;
  isStatusShown: boolean;
  isEmailShown: boolean;
  /**
   * Controls the next-up row on the card (follow-up / deadline chip). The
   * key was historically named `isCourseShown`; it now drives the follow-up
   * row on the card and the (still-named) "Course" column in the table.
   */
  isFollowUpShown: boolean;
  /** Show aggregated payment status chip on the card footer. */
  isPaymentStatusShown: boolean;
  isLeadSourceShown: boolean;
  isCampaignShown: boolean;
  isSubstatusShown: boolean;
  isPriorityShown: boolean;
  isLastUpdatedOnShown: boolean;
  /**
   * The lead's real `createdAt`, labelled. The card's corner date follows
   * the active sort key (see `pickCardSortDate`), so it isn't always the
   * creation date — this row always is. Off by default; blobs saved before
   * the key existed pick up that default via the hydrate/persist merge.
   */
  isCreatedAtShown: boolean;
  isLastCallRecordShown: boolean;
  isUsersShown: boolean;
  isRemarksShown: boolean;
  isLeadScoreShown: boolean;
  /**
   * Per-tenant CustomInput ids the user wants displayed on each lead card.
   * Keys are stringified customInputId; value `true` = show. Persisted via
   * redux-persist so picks survive reloads. Drives the `cfShow` query
   * param on the list endpoint, which in turn triggers the server's
   * `attachLeadCardExtras` batch fetch (zero overhead when map is empty).
   */
  customFieldToggles: Record<string, boolean>;
  /**
   * Per-industry niche modules the user wants displayed on each card
   * (e.g. "course", "university"). Keyed by `nicheModelName`. Mirrors the
   * `customFieldToggles` shape. Drives the `moduleShow` query param.
   */
  moduleToggles: Record<string, boolean>;
  /**
   * Tenant roles the user wants surfaced on each card/table/board as an
   * assignee column (e.g. show the Counsellor assigned to the lead/deal).
   * Keyed by stringified roleId. Mirrors `moduleToggles`. Drives the
   * `roleShow` query param, which makes the server attach `cardRoles`
   * (lead- + deal-level assignees) per lead row.
   */
  roleToggles: Record<string, boolean>;
  /**
   * List view: the user's drag-and-drop column arrangement, as the ordered
   * list of column keys last dropped (see `utils/listColumnOrder.ts`).
   * Absent = default layout. Persisted per user via the view-preference
   * write-through, same as the toggles above.
   */
  listColumnOrder?: string[];
  /**
   * List view: the Excel-style header menus' state — ticked values per
   * column key and the active sort — saved per user like everything else on
   * this slice, so a narrowed list survives reloads and devices.
   */
  listColumnFilters?: Record<string, string[]>;
  listColumnSort?: { key: string; dir: "asc" | "desc" } | null;
  /** List view: column widths in px the user dragged, by column key. */
  listColumnWidths?: Record<string, number>;
  /**
   * List view: FROZEN columns (Google-Sheets style) — keys the user pinned.
   * Pinned columns render first and stay put while the rest scroll sideways.
   */
  listColumnPins?: string[];
}

/* ================= INITIAL STATE ================= */

// New users land on a populated grid — only the niche/data-heavy
// columns (payment, last-call recording, lead score) stay off by
// default so the card doesn't feel cluttered out of the box.
// Existing users keep whatever they already chose via redux-persist.
const initialState: LeadCardFunctionality = {
  isNameShown: true,
  isMobileNumberShown: true,
  isBranchShown: true,
  isUsersShown: true,
  isStageShown: true,
  isStatusShown: true,
  isEmailShown: true,
  isFollowUpShown: true,
  isLeadSourceShown: true,
  isCampaignShown: true,
  isSubstatusShown: true,
  isPriorityShown: true,
  isLastUpdatedOnShown: true,
  isRemarksShown: true,
  isPaymentStatusShown: false,
  isLastCallRecordShown: false,
  isLeadScoreShown: false,
  isCreatedAtShown: false,
  customFieldToggles: {},
  moduleToggles: {},
  roleToggles: {},
};

/* ================= SLICE ================= */

const leadCardFunctionalitySlice = createSlice({
  name: "leadCardFunctionality",
  initialState,
  reducers: {
    setLeadBranchToggle(state, action: PayloadAction<boolean>) {
      state.isBranchShown = action.payload;
    },
    setLeadStatusToggle(state, action: PayloadAction<boolean>) {
      state.isStatusShown = action.payload;
    },
    setLeadEmailToggle(state, action: PayloadAction<boolean>) {
      state.isEmailShown = action.payload;
    },
    setLeadFollowUpToggle(state, action: PayloadAction<boolean>) {
      state.isFollowUpShown = action.payload;
    },
    setLeadPaymentStatusToggle(state, action: PayloadAction<boolean>) {
      state.isPaymentStatusShown = action.payload;
    },
    setLeadLeadSourceToggle(state, action: PayloadAction<boolean>) {
      state.isLeadSourceShown = action.payload;
    },
    setLeadCampaignToggle(state, action: PayloadAction<boolean>) {
      state.isCampaignShown = action.payload;
    },
    setLeadSubstatusToggle(state, action: PayloadAction<boolean>) {
      state.isSubstatusShown = action.payload;
    },
    setLeadPriorityToggle(state, action: PayloadAction<boolean>) {
      state.isPriorityShown = action.payload;
    },
    setLeadLastUpdatedOnToggle(state, action: PayloadAction<boolean>) {
      state.isLastUpdatedOnShown = action.payload;
    },
    setLeadCreatedAtToggle(state, action: PayloadAction<boolean>) {
      state.isCreatedAtShown = action.payload;
    },
    setLeadLastCallRecordToggle(state, action: PayloadAction<boolean>) {
      state.isLastCallRecordShown = action.payload;
    },
    setLeadUsersToggle(state, action: PayloadAction<boolean>) {
      state.isUsersShown = action.payload;
    },
    setLeadRemarksToggle(state, action: PayloadAction<boolean>) {
      state.isRemarksShown = action.payload;
    },
    setLeadLeadScoreToggle(state, action: PayloadAction<boolean>) {
      state.isLeadScoreShown = action.payload;
    },
    toggleLeadCustomField(
      state,
      action: PayloadAction<{ id: number | string; on: boolean }>,
    ) {
      const key = String(action.payload.id);
      if (action.payload.on) {
        state.customFieldToggles[key] = true;
      } else {
        delete state.customFieldToggles[key];
      }
    },
    toggleLeadModule(
      state,
      action: PayloadAction<{ name: string; on: boolean }>,
    ) {
      const key = action.payload.name;
      if (action.payload.on) {
        state.moduleToggles[key] = true;
      } else {
        delete state.moduleToggles[key];
      }
    },
    toggleLeadRole(
      state,
      action: PayloadAction<{ id: number | string; on: boolean }>,
    ) {
      // Guard against an undefined map on blobs persisted before the
      // roleToggles key existed (the v4 migration backfills it, but be safe).
      if (!state.roleToggles) state.roleToggles = {};
      const key = String(action.payload.id);
      if (action.payload.on) {
        state.roleToggles[key] = true;
      } else {
        delete state.roleToggles[key];
      }
    },
    /** List view header drag-and-drop — the full ordered key list. */
    setLeadListColumnOrder(state, action: PayloadAction<string[]>) {
      state.listColumnOrder = action.payload;
    },
    /** Header menu: ticked values for one column (null = no filter). */
    setLeadListColumnFilter(
      state,
      action: PayloadAction<{ key: string; selected: string[] | null }>,
    ) {
      const next = { ...(state.listColumnFilters ?? {}) };
      if (action.payload.selected) next[action.payload.key] = action.payload.selected;
      else delete next[action.payload.key];
      state.listColumnFilters = Object.keys(next).length ? next : undefined;
    },
    /** Header menu: the active sort (null = none). */
    setLeadListColumnSort(
      state,
      action: PayloadAction<{ key: string; dir: "asc" | "desc" } | null>,
    ) {
      state.listColumnSort = action.payload;
    },
    /** Header resize handle — width in px for one column (null = default). */
    setLeadListColumnWidth(
      state,
      action: PayloadAction<{ key: string; width: number | null }>,
    ) {
      const next = { ...(state.listColumnWidths ?? {}) };
      if (action.payload.width == null) delete next[action.payload.key];
      else next[action.payload.key] = Math.round(action.payload.width);
      state.listColumnWidths = Object.keys(next).length ? next : undefined;
    },
    /** Header pin button — freeze / unfreeze one column. */
    toggleLeadListColumnPin(state, action: PayloadAction<string>) {
      const pins = state.listColumnPins ?? [];
      const next = pins.includes(action.payload)
        ? pins.filter((k) => k !== action.payload)
        : [...pins, action.payload];
      state.listColumnPins = next.length ? next : undefined;
    },
    clearLeadListColumnFilters(state) {
      state.listColumnFilters = undefined;
      state.listColumnSort = null;
    },
    /**
     * Reset every standard toggle + clear the custom-field / module maps
     * back to the factory defaults. Used by the "Reset Layouts" menu item
     * on the Leads header. The slice replaces state wholesale rather than
     * mutating individual keys so a stale persisted blob from an older
     * shape gets fully cleaned up too.
     */
    resetLeadCardLayout(_state, action: PayloadAction<unknown | undefined>) {
      // Reset means "back to the WORKSPACE layout" when the tenant has one —
      // dropping an admin-set arrangement for the factory defaults is what
      // made a shared layout look like it never stuck. No workspace layout
      // configured → factory defaults, as before.
      const workspace = action?.payload;
      if (workspace && typeof workspace === "object") {
        return { ...initialState, ...(workspace as Partial<LeadCardFunctionality>) };
      }
      return { ...initialState };
    },
    /**
     * Replace the layout with the user's DB-saved blob on boot. Merged over
     * `initialState` so any key the server doesn't carry (older/newer shape)
     * falls back to a sane default. Dispatched once, after auth verify; the
     * write-through sync middleware ignores this action so hydrating never
     * echoes a save back to the server.
     */
    hydrateLeadCardLayout(_state, action: PayloadAction<unknown>) {
      const payload = action.payload;
      if (!payload || typeof payload !== "object") {
        return { ...initialState };
      }
      return { ...initialState, ...(payload as Partial<LeadCardFunctionality>) };
    },
  },
});

export const {
  setLeadBranchToggle,
  setLeadStatusToggle,
  setLeadEmailToggle,
  setLeadFollowUpToggle,
  setLeadPaymentStatusToggle,
  setLeadLeadSourceToggle,
  setLeadCampaignToggle,
  setLeadSubstatusToggle,
  setLeadPriorityToggle,
  setLeadLastUpdatedOnToggle,
  setLeadCreatedAtToggle,
  setLeadLastCallRecordToggle,
  setLeadUsersToggle,
  setLeadRemarksToggle,
  setLeadLeadScoreToggle,
  toggleLeadCustomField,
  toggleLeadModule,
  toggleLeadRole,
  setLeadListColumnOrder,
  setLeadListColumnFilter,
  setLeadListColumnSort,
  clearLeadListColumnFilters,
  setLeadListColumnWidth,
  toggleLeadListColumnPin,
  resetLeadCardLayout,
  hydrateLeadCardLayout,
} = leadCardFunctionalitySlice.actions;
export default leadCardFunctionalitySlice.reducer;
